import type { ApiClient } from 'grabbitbot-legacy-ui'
import type {
  BalanceVsMopRow,
  ClosedPositionOfCoin,
  ClosedPositionSearchItem,
  ClosedPriceResponse,
  Commission,
  GlobalProfitByCoin,
  LifetimeProfit,
  OpenPosition,
  PositionPage,
  ProfitAllAssets,
  ProfitByCoin,
  ProfitSummary,
  Transaction,
  TransactionSearch,
  Treasury,
  UsageFee,
  WalletRow,
} from './statisticsTypes'

export type Resolution = 'daily' | 'hourly'

type Query = Record<string, string | number | boolean | undefined | null>

/** `tags[]=a&tags[]=b` repeated-key query params — `ApiClient`'s own `query`
 * option (`RequestOptions.query`) only ever serializes one value per key
 * (see `grabbitbot-legacy-ui/src/api-client/client.ts` `formatUrl`), so a
 * multi-value filter is built here and glued onto the path before the params
 * substitution runs. */
function withQuery(query?: Query, arrayParams?: Record<string, string[] | undefined>): string {
  const parts: string[] = []
  if (query) {
    for (const key of Object.keys(query)) {
      const value = query[key]
      if (value === undefined || value === null || value === '') continue
      parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
    }
  }
  if (arrayParams) {
    for (const key of Object.keys(arrayParams)) {
      for (const value of arrayParams[key] ?? []) {
        parts.push(`${encodeURIComponent(key)}[]=${encodeURIComponent(value)}`)
      }
    }
  }
  return parts.length > 0 ? `?${parts.join('&')}` : ''
}

/**
 * B3 domain client — ports `V3SpotStatisticsService`/`V3SpotBotService`'s
 * statistics + read-only bot-view methods (`src/app/data/api-client/v3/
 * v3-spot-statistics.service.ts`, `v3-spot-bot.service.ts` in the legacy
 * archive) plus the two legacy (non-v3) `/statistics/...` reads (closed
 * price, balance-vs-mop). Trading type is always `spot` (owner 2026-09-26:
 * marginbot is not rewritten). See legacy-migration
 * `docs/analysis/2026-09-27-grabbitbot-b3-contract.md` for the pinned shapes.
 *
 * Every read here is `cache: false` — this whole batch is live
 * dashboard/statistics data the user expects to refresh on every visit
 * (legacy re-issued the HTTP call every time too; no client cache existed on
 * this half of the app), and the closed-price feed's whole point is "the
 * latest candle".
 */
export function createStatisticsClient(client: ApiClient) {
  return {
    // --- profit -----------------------------------------------------------
    /** #1/#2 — one coin pair's (or, without `coin1`, every pair sharing
     * `coin2`'s) profit history. */
    profit: (exchangeId: string, resolution: Resolution, coin2: string, coin1?: string, days?: number) =>
      client.get<ProfitSummary>(
        coin1
          ? '/v3/spot/statistics/{exchangeId}/profit/{resolution}/{coin1}/{coin2}'
          : '/v3/spot/statistics/{exchangeId}/profit/{resolution}/{coin2}',
        {
          params: { exchangeId, resolution, coin2, ...(coin1 ? { coin1 } : {}) },
          query: { days },
          cache: false,
        },
      ),
    /** #3 — lifetime profit, `exchangeId` `-` = every exchange. */
    lifetimeProfit: (exchangeId: string) =>
      client.get<LifetimeProfit>('/v3/spot/statistics/{exchangeId}/profit/lifetime', {
        params: { exchangeId },
        cache: false,
      }),
    /** #4 — profit grouped by coin for one quote asset (Dashboard's data
     * source: `all` + `byCoin[]` + `top10/20/50` buckets). */
    profitByCoin: (exchangeId: string, resolution: Resolution, coin2: string, days?: number) =>
      client.get<ProfitByCoin>('/v3/spot/statistics/{exchangeId}/profit/bycoin/{resolution}/{coin2}', {
        params: { exchangeId, resolution, coin2 },
        query: { days },
        cache: false,
      }),
    /** #5 — profit grouped by coin across every quote asset, optional
     * `strategyId` filter (positions bought AND sold by it, not sold
     * manually — a non-numeric value is not a filter, legacy quirk kept). */
    profitAllAssets: (exchangeId: string, resolution: Resolution, days?: number, strategyId?: string) =>
      client.get<ProfitAllAssets>('/v3/spot/statistics/{exchangeId}/profit/bycoin/{resolution}', {
        params: { exchangeId, resolution },
        query: { days, strategyId },
        cache: false,
      }),

    // --- treasury / billing -------------------------------------------------
    /** #6 — balance history per coin; `coin1` narrows to one coin's history. */
    treasury: (exchangeId: string, resolution: Resolution, days?: number, coin1?: string) =>
      client.get<Treasury>('/v3/spot/statistics/{exchangeId}/treasury/bycoin/{resolution}', {
        params: { exchangeId, resolution },
        query: { days, coin1 },
        cache: false,
      }),
    /** #7 — `month`: `0` this month, `±N` relative, or `YYYYMM` absolute. */
    commissionMonth: (month?: string | number) =>
      client.get<Commission>('/v3/spot/statistics/commission/month', { query: { month }, cache: false }),
    /** #8 — same `month` convention; months before 2025-01 answer `exchanges: []`. */
    usageFeeMonth: (month?: string | number) =>
      client.get<UsageFee>('/v3/spot/statistics/usagefee/month', { query: { month }, cache: false }),
    /** #9 — like #4, but EVERY user's profit (no `a`/`startTable`); a bucket
     * ending "today" is intentionally excluded (still in progress). */
    globalProfitByCoin: (exchangeId: string, resolution: Resolution, coin2: string, days?: number) =>
      client.get<GlobalProfitByCoin>('/v3/spot/statistics/{exchangeId}/global-profit/bycoin/{resolution}/{coin2}', {
        params: { exchangeId, resolution, coin2 },
        query: { days },
        cache: false,
      }),
    /** #10 — no pagination envelope; the tag filter is a bound parameter
     * server-side (ticket 0c6036f1 — an injection-shaped tag only matches a
     * row carrying it verbatim, never widens the result). */
    transactions: (
      exchangeId: string,
      filter: { days?: number; coin1?: string; coin2?: string; tag?: string } = {},
    ) =>
      client.get<TransactionSearch>('/v3/spot/statistics/{exchangeId}/transactions', {
        params: { exchangeId },
        query: filter,
        cache: false,
      }),

    // --- bot positions (Datastore-backed) -----------------------------------
    /** #11 — `type`: `open` / `closed` / `closedwithloss` (anything else
     * answers `{items: []}`). `cursor` is an opaque offset string — pass it
     * back as `start`; an old/foreign cursor restarts at page 1. Datastore-
     * backed: closed/closedwithloss items share the SAME shape as #13/#14
     * (`ClosedPositionOfCoin`), not the BigQuery shape #12 returns. */
    positions: (
      exchangeId: string,
      type: 'open' | 'closed' | 'closedwithloss',
      opts: { pageSize?: number; start?: string; tags?: string[] } = {},
    ) =>
      client.get<PositionPage<OpenPosition | ClosedPositionOfCoin>>(
        '/v3/spot/bot/{exchangeId}/positions/{type}' +
          withQuery({ pageSize: opts.pageSize, start: opts.start }, { tags: opts.tags }),
        { params: { exchangeId, type }, cache: false },
      ),
    /** #12 — BigQuery-backed search; `type` is a free-form bucket name
     * (`withloss`, `off-handler`, `manual`, `light`, `plain`, or a legacy
     * closed-position kind), not the `type` of #11. */
    closedPositionSearch: (
      exchangeId: string,
      type: string,
      opts: { pageSize?: number; start?: string; coin1?: string; coin2?: string } = {},
    ) =>
      client.get<PositionPage<ClosedPositionSearchItem>>(
        '/v3/spot/bot/{exchangeId}/positions/closed/search/{type}' + withQuery(opts as Query),
        { params: { exchangeId, type }, cache: false },
      ),
    /** #13 — oldest first, plain array, no pagination. */
    openPositionsOfCoin: (exchangeId: string, coin1: string, coin2: string, days?: number) =>
      client.get<OpenPosition[]>('/v3/spot/bot/{exchangeId}/{coin1}/{coin2}/positions/open', {
        params: { exchangeId, coin1, coin2 },
        query: { days },
        cache: false,
      }),
    /** #14 — oldest first, plain array, no pagination. */
    closedPositionsOfCoin: (exchangeId: string, coin1: string, coin2: string, days?: number) =>
      client.get<ClosedPositionOfCoin[]>('/v3/spot/bot/{exchangeId}/{coin1}/{coin2}/positions/closed', {
        params: { exchangeId, coin1, coin2 },
        query: { days },
        cache: false,
      }),

    // --- legacy (non-v3) reads ----------------------------------------------
    /** #16 — candle.grabbitbot.com passthrough, no auth required — never
     * send a bearer token here (the backend route itself is public). */
    closedPrice: (exchangeId: string, tradingType: string, coin1: string, coin2: string, interval: string) =>
      client.get<ClosedPriceResponse>(
        '/statistics/{exchangeId}/{tradingType}/coin/{coin1}/{coin2}/closedprice/{interval}',
        { params: { exchangeId, tradingType, coin1, coin2, interval }, auth: false, cache: false },
      ),
    /** #17 — legacy's real "Wallet" nav item (`v3-header.component.html`
     * labels it "Wallet", but it routes to `.../statistics/stuck-coins` and
     * calls this balance-vs-mop read, NOT `/v3/spot/bot/{ex}/wallet`). */
    /** #15 — open-position sums (spotbot_read) vs the exchange balance
     * (spotbot), valued in USD. The Wallet / stuck-coins page reads THIS since
     * 0.14.2: #17 needs the legacy GCP exchangeinterface, which the new
     * system does not run (it answered 503 `exchangeinterface_not_configured`),
     * and its open-position half was always empty in legacy. Same row shape. */
    wallet: (exchangeId: string) =>
      client.get<WalletRow[]>('/v3/spot/bot/{exchangeId}/wallet', { params: { exchangeId }, cache: false }),
    balanceVsMop: (exchangeId: string, tradingType: string) =>
      client.get<BalanceVsMopRow[]>('/statistics/{exchangeId}/{tradingType}/coin/balance-vs-mop', {
        params: { exchangeId, tradingType },
        cache: false,
      }),
  }
}

export type StatisticsClient = ReturnType<typeof createStatisticsClient>
export type { Transaction }
